import axios from 'axios';
import SessionStorage from '../SessionStorage';
import { YoutubeVideo, YoutubeApiResponse, YoutubePlaylistItem, YoutubePlaylist, IYoutubeResourceIdBean, YoutubeSnippet, YoutubeItem, YoutubeItemWithDescriptionOverride, YoutubeSnippetVideo } from './YoutubeTypes/YoutubeTypes';


export enum YoutubeConstants {
    key = 'youtubeKey',
    storageKey = 'ytbcche',
}

export const findVideoId = (youtubeUrl: string) => {
    const re = /(?:youtube(?:-nocookie)?\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/gi;
    const foundVideoId = re.exec(youtubeUrl);
    return foundVideoId;
};

export const checkPersistedData = <T = (YoutubeVideo | YoutubePlaylistItem[])>(youtubeId?: string): T | null => {
    const videoDataCache = SessionStorage.tryGetObject(YoutubeConstants.storageKey, {});
    if (videoDataCache[youtubeId]) {
        return videoDataCache[youtubeId];
    } else {
        return null;
    }
}

export const persistData = async (youtubeVideoId: string, videoData: YoutubeItem | YoutubeItem[]) => {
    const videoDataCache = SessionStorage.tryGetObject(YoutubeConstants.storageKey, {});
    if (!videoDataCache[youtubeVideoId] && videoData) {
        videoDataCache[youtubeVideoId] = videoData;
        SessionStorage.setObject(YoutubeConstants.storageKey, videoDataCache);
    }
};

export const initialiseSingleVideoData = async (youtubeVideoId: string, youtubeApiKey: string) => {
    const check = checkPersistedData<YoutubeVideo>(youtubeVideoId);
    if (check) {
        return check;
    }
    const youtubeApiUrl = `https://www.googleapis.com/youtube/v3/videos?part=snippet,contentDetails&id=${youtubeVideoId}&key=${youtubeApiKey}`;
    try {
        const response = await axios.get<YoutubeApiResponse<YoutubeVideo>>(youtubeApiUrl);
        const retrievedVideoData = response?.data?.items[0];
        if (retrievedVideoData) {
            persistData(youtubeVideoId, retrievedVideoData);

            const schemaCode = document.createElement('script');
            schemaCode.type = 'application/ld+json';
            schemaCode.innerHTML = JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'VideoObject',
                'name': `${retrievedVideoData.snippet.title}`,
                'description': `${retrievedVideoData.snippet.description}`,
                'thumbnailUrl': `${retrievedVideoData.snippet.thumbnails.default.url}`,
                'duration': `${retrievedVideoData.contentDetails.duration}`,
                'contentUrl': `https://www.youtube.com/watch?v=${retrievedVideoData.id}`,
                'embedUrl': `https://www.youtube.com/embed/${retrievedVideoData.id}`,
                'uploadDate': `${retrievedVideoData.snippet.publishedAt}`,
                'potentialAction': {
                    '@type': 'SeekToAction',
                    'target': `https://www.youtube.com/watch?v=${retrievedVideoData.id}&t={seek_to_second_number}`,
                    'startOffset-input': 'required name=seek_to_second_number',
                },
            });
            document.head.append(schemaCode);
            return retrievedVideoData;
        }
        throw response;
        
    } catch (e) {
        console.error('Could not retrieve video data');
    }
};

export const initialisePlaylistData = async (youtubePlaylistLink: string, youtubeApiKey?: string) => {
    const check = checkPersistedData<YoutubePlaylistItem[]>(youtubePlaylistLink);
    if (check) {
        return check;
    }
    let youtubeApiUrl = youtubePlaylistLink;
    if (!youtubeApiUrl.includes("youtube") && youtubeApiKey) {
        youtubeApiUrl = `https://www.googleapis.com/youtube/v3/playlistItems?playlistId=${youtubePlaylistLink}&part=snippet&maxResults=50&key=${youtubeApiKey}`
    } else if (!youtubeApiUrl.includes("key=") && youtubeApiKey) {
        youtubeApiUrl = youtubeApiUrl + `&key=${youtubeApiKey}`
    }
    try {
        const response = await axios.get(youtubeApiUrl);
        const result = filterOutVideos(response.data.items);
        persistData(youtubePlaylistLink, result);
        return result;
    } catch (ignored) {
        return {} as YoutubePlaylistItem[];
    }
}

export const initialiseAndUpdateYoutubeVideoCache = async (beans: IYoutubeResourceIdBean[], youtubeApiKey: string): Promise<Record<string, YoutubeItemWithDescriptionOverride & YoutubeSnippetVideo>> => {
    const youtubeCache = {} as Record<string, YoutubeItemWithDescriptionOverride & YoutubeSnippetVideo>;

    if (!beans || !Array.isArray(beans)) {
        return SessionStorage.tryGetObject<Record<string, YoutubeItemWithDescriptionOverride & YoutubeSnippetVideo>>(
            YoutubeConstants.storageKey,
            {}
        );
    }

    for (const bean of beans) {
        if (!bean) {
            continue;
        }

        const youtubeResourceId = bean.youtubeResourceId?.trim();
        if (!youtubeResourceId) {
            continue;
        }

        if (bean.youtubePlaylistToggle) {
            const data = await initialisePlaylistData(youtubeResourceId, youtubeApiKey);
            for (const playlistItem of data) {
                youtubeCache[playlistItem.snippet.resourceId.videoId] = {
                    ...youtubeCache[playlistItem.snippet.resourceId.videoId],
                    ...playlistItem,
                } as any;
            }
        } else if (bean.youtubeDescriptionOverride || bean.youtubeThumbnailOverridePath) {
            youtubeCache[youtubeResourceId] = {
                ...youtubeCache[youtubeResourceId],
                descriptionOverride: bean.youtubeDescriptionOverride,
                thumbnailOverride: bean.youtubeThumbnailOverridePath,
            };
        } else {
            youtubeCache[youtubeResourceId] = null;
        }
    }

    const videoDataCache = SessionStorage.tryGetObject<Record<string,YoutubeItemWithDescriptionOverride & YoutubeSnippetVideo>>(YoutubeConstants.storageKey, {});
    for (const cachedItem of Object.keys(videoDataCache)) {
        if (youtubeCache[cachedItem] === null || !youtubeCache[cachedItem]?.snippet) {
            youtubeCache[cachedItem] = {
                ...videoDataCache[cachedItem],
                ...youtubeCache[cachedItem],
            }
        }
    }

    const youtubeIdsToSearch = Object.keys(youtubeCache).filter((element) => youtubeCache[element] === null || !youtubeCache[element].snippet).join(',');
    if (youtubeIdsToSearch.length) {
        const youtubeApiUrl = `https://www.googleapis.com/youtube/v3/videos?part=snippet&id=${youtubeIdsToSearch}&key=${youtubeApiKey}`
        const response = await axios.get<YoutubeApiResponse<YoutubeVideo>>(youtubeApiUrl);
        for (const item of response.data.items) {
            youtubeCache[item.id] = {...youtubeCache[item.id], ...item};
        };
    }

    const nextVideoDataCache = {...videoDataCache, ...youtubeCache};
    SessionStorage.setObject(YoutubeConstants.storageKey, nextVideoDataCache);
    return nextVideoDataCache;
};

export const filterOutVideos = (videos: YoutubePlaylistItem[]) => {
    const filteredDescriptions = ['This video is private.', 'This video is unavailable.'];
    return videos.filter((video) => !(filteredDescriptions.includes(video?.snippet?.description)));
};

export const reorderVideos = (listToReorder: YoutubeItem[], listToOrderWith: IYoutubeResourceIdBean[]) => {
    const flattenedOrderingList = listToOrderWith.map((element) => element.youtubeResourceId);
    const nextPlaylistData = listToReorder.sort(
        (left, right) => {
            const leftIndex = flattenedOrderingList.indexOf(left.id);
            const rightIndex = flattenedOrderingList.indexOf(right.id);
            if (
                (leftIndex !== -1 && rightIndex === -1)
                || (leftIndex === -1 && rightIndex !== -1)
            ) {
                return -1;
            } else if(leftIndex !== -1 && rightIndex !== -1) {
                return leftIndex - rightIndex;
            }
            return 0;
        });
    return nextPlaylistData;
}
